import { useCallback, useEffect, useState } from 'react';
import LocationApi from '../services/location';
import { useRef } from 'react';

export function useLocationSearch(options = {}) {
    const { initialQuery = '', debounceMs = 350, minLength = 2 } = options;

    const previousQueryRef = useRef(null);
    const [query, setQuery] = useState(initialQuery);
    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchLocations = useCallback(
        async searchQuery => {
            const trimmedSearchQuery = searchQuery.trim();
            if (
                previousQueryRef.current === trimmedSearchQuery ||
                trimmedSearchQuery.length < minLength
            ) {
                previousQueryRef.current = trimmedSearchQuery;
                setLocations([]);
                setIsLoading(false);
                setError(null);
                return;
            }

            try {
                previousQueryRef.current = trimmedSearchQuery;
                setIsLoading(true);
                setError(null);

                const locationResults =
                    await LocationApi.search(trimmedSearchQuery);

                setLocations(locationResults);
            } catch (e) {
                setError('Failed to fetch locations');
            } finally {
                setIsLoading(false);
            }
        },
        [minLength]
    );

    useEffect(() => {
        if (
            !previousQueryRef.current ||
            previousQueryRef.current === query ||
            query.trim().length < minLength
        ) {
            previousQueryRef.current = query;
            setLocations([]);
            setIsLoading(false);
            setError(null);
            return;
        }

        const timeout = setTimeout(() => {
            fetchLocations(query);
        }, debounceMs);

        return () => clearTimeout(timeout);
    }, [query, debounceMs, minLength, fetchLocations]);

    return {
        query,
        setQuery,
        locations,
        isLoading,
        error
    };
}
